import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Checkbox from "@material-ui/core/Checkbox";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import FilterListIcon from "@material-ui/icons/FilterList";

import { darken } from "@material-ui/core/styles/colorManipulator";

import { deepOrange, green } from "@material-ui/core/colors";

function desc(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function getSorting(order, orderBy) {
  return order === "desc"
    ? (a, b) => desc(a, b, orderBy)
    : (a, b) => -desc(a, b, orderBy);
}

const rows = [
  { id: "sourceName", numeric: false, disablePadding: false, label: "Name" },
  { id: "message", numeric: false, disablePadding: false, label: "Message" },
  { id: "timestamp", numeric: false, disablePadding: false, label: "Time" },
  {
    id: "acknowledged",
    numeric: false,
    disablePadding: false,
    label: "Acknowledged"
  },
  { id: "state", numeric: false, disablePadding: false, label: "State" }
];

class HealthTableHead extends React.Component {
  createSortHandler = property => event => {
    this.props.onRequestSort(event, property);
  };

  render() {
    const {
      onSelectAllClick,
      order,
      orderBy,
      numSelected,
      rowCount
    } = this.props;

    return (
      <TableHead>
        <TableRow style={{ fontSize: ".75em", fontVarinat: "all-petite-caps" }}>
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={numSelected === rowCount}
              onChange={onSelectAllClick}
            />
          </TableCell>
          {rows.map(
            row => (
              <TableCell
                key={row.id}
                align={row.numeric ? "right" : "left"}
                padding={row.disablePadding ? "none" : "default"}
                sortDirection={orderBy === row.id ? order : false}>
                <Tooltip
                  title="Sort"
                  placement={row.numeric ? "bottom-end" : "bottom-start"}
                  enterDelay={300}>
                  <TableSortLabel
                    style={{
                      fontSize: ".75em",
                      fontVarinat: "all-petite-caps"
                    }}
                    active={orderBy === row.id}
                    direction={order}
                    onClick={this.createSortHandler(row.id)}>
                    {row.label}
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
            ),
            this
          )}
        </TableRow>
      </TableHead>
    );
  }
}

HealthTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired
};

const toolbarStyles = theme => ({
  root: {
    paddingRight: theme.spacing.unit
  },
  highlight:
    theme.palette.type === "light"
      ? {
          color: theme.palette.primary.main,
          backgroundColor: darken(theme.palette.primary.light, 0.85)
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark
        },
  spacer: {
    flex: "1 1 100%"
  },
  actions: {
    color: theme.palette.text.secondary
  },
  title: {
    flex: "0 0 auto",
    fontSize: "1em",
    fontVariant: "all-small-caps"
  }
});

const handleAcknowledge = (acknowledge, selected, clearAll) => {
  selected.forEach(GUID => acknowledge(GUID));
  clearAll();
};

let HealthTableToolbar = props => {
  const { classes, numSelected, selected, acknowledge, clearAll } = props;

  return (
    <Toolbar
      className={classNames(classes.root, {
        [classes.highlight]: numSelected > 0
      })}>
      <div className={classes.title}>
        {numSelected > 0 ? (
          <Typography color="inherit" variant="subtitle1">
            {numSelected} selected
          </Typography>
        ) : (
          <Typography variant="h6" id="tableTitle">
            Building Health
          </Typography>
        )}
      </div>
      <div className={classes.spacer} />
      <div className={classes.actions}>
        {numSelected > 0 ? (
          <Tooltip title="Acknowledge">
            <IconButton
              aria-label="Acknowledge"
              onClick={() =>
                handleAcknowledge(acknowledge, selected, clearAll)
              }>
              <CheckCircleIcon style={{ color: green[200] }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Filter list">
            <IconButton aria-label="Filter list">
              <FilterListIcon />
            </IconButton>
          </Tooltip>
        )}
      </div>
    </Toolbar>
  );
};

HealthTableToolbar.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired
};

HealthTableToolbar = withStyles(toolbarStyles)(HealthTableToolbar);

const styles = theme => ({
  root: {
    width: "100%",
    marginTop: theme.spacing.unit * 3
  },
  table: {
    minWidth: 1020
  },
  tableWrapper: {
    overflowX: "auto",
    fontVariant: "all-petite-caps"
  },
  acknowledged: {
    color: green[200]
  },
  alarmState: {
    color: deepOrange[200]
  },
  alarm: {
    background: deepOrange[200]
  },
  hover: {
    "&$hover:hover": {
      backgroundColor: darken(deepOrange[200], 0.15)
    }
  }
});

class HealthTable extends React.Component {
  state = {
    order: "asc",
    orderBy: "state",
    selected: [],
    data: this.props.data,
    page: 0,
    rowsPerPage: 50
  };

  componentDidUpdate(prevProps) {
    const { data } = this.props;

    if (prevProps.data !== data) {
      this.setState({
        data
      });
    }
  }

  handleRequestSort = (event, property) => {
    const orderBy = property;
    let order = "desc";

    if (this.state.orderBy === property && this.state.order === "desc") {
      order = "asc";
    }

    this.setState({ order, orderBy });
  };

  handleSelectAllClick = event => {
    if (event.target.checked) {
      this.setState(state => ({ selected: state.data.map(n => n.GUID) }));
      return;
    }
    this.setState({ selected: [] });
  };

  handleClick = (event, id) => {
    const { selected } = this.state;
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    this.setState({ selected: newSelected });
  };

  handleChangePage = (event, page) => {
    this.setState({ page });
  };

  handleChangeRowsPerPage = event => {
    this.setState({ rowsPerPage: event.target.value });
  };

  clearAll = () => {
    this.setState({ selected: [] });
  };

  isSelected = id => this.state.selected.indexOf(id) !== -1;

  render() {
    const { classes, acknowledge } = this.props;
    const { data, order, orderBy, selected, rowsPerPage, page } = this.state;
    const emptyRows =
      rowsPerPage - Math.min(rowsPerPage, data.length - page * rowsPerPage);

    return (
      <Paper className={classes.root}>
        <HealthTableToolbar
          numSelected={selected.length}
          acknowledge={acknowledge}
          clearAll={this.clearAll.bind(this)}
          selected={this.state.selected}
        />
        <div className={classes.tableWrapper}>
          <Table className={classes.table} aria-labelledby="tableTitle">
            <HealthTableHead
              style={{ fontSize: "1em", fontVarinat: "all-petite-caps" }}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={this.handleSelectAllClick}
              onRequestSort={this.handleRequestSort}
              rowCount={data.length}
            />
            <TableBody style={{ fontSize: ".75em" }}>
              {stableSort(data, getSorting(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(n => {
                  const isSelected = this.isSelected(n.GUID);
                  return (
                    <TableRow
                      className={classNames({
                        [classes.alarm]: !n.acknowledged && n.state === "ALARM"
                      })}
                      style={{
                        cursor: "pointer",
                        fontSize: ".75em",
                        fontVariant: "all-petite-caps",
                        lineHeight: "1"
                      }}
                      hover
                      classes={{
                        hover:
                          !n.acknowledged && n.state === "ALARM"
                            ? classes.hover
                            : classes.hoverNormal
                      }}
                      onClick={event => this.handleClick(event, n.GUID)}
                      role="checkbox"
                      aria-checked={isSelected}
                      tabIndex={-1}
                      key={n.GUID}
                      selected={isSelected}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={isSelected} />
                      </TableCell>
                      <TableCell component="th" scope="row" padding="none">
                        {n.sourceName}
                      </TableCell>
                      <TableCell component="th" scope="row" padding="none">
                        {n.message}
                      </TableCell>
                      <TableCell align="right">
                        {n.timestamp
                          ? `${new Date(
                              n.timestamp
                            ).toLocaleDateString()} ${new Date(
                              n.timestamp
                            ).toLocaleTimeString()}`
                          : " "}
                      </TableCell>
                      <TableCell
                        align="right"
                        className={classNames({
                          [classes.acknowledged]: n.acknowledged,
                          [classes.alarmState]:
                            !n.acknowledged && n.state === "ALARM"
                        })}>
                        {String(n.acknowledged)}
                      </TableCell>
                      <TableCell
                        align="right"
                        className={classNames({
                          [classes.alarmState]:
                            !n.acknowledged && n.state === "ALARM"
                        })}>
                        {n.state}
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 49 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={[50, 100, 150]}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{
            "aria-label": "Previous Page"
          }}
          nextIconButtonProps={{
            "aria-label": "Next Page"
          }}
          onChangePage={this.handleChangePage}
          onChangeRowsPerPage={this.handleChangeRowsPerPage}
        />
      </Paper>
    );
  }
}

HealthTable.propTypes = {
  classes: PropTypes.object.isRequired,
  acknowledge: PropTypes.func.isRequired
};

export default withStyles(styles)(HealthTable);
